<template>
   <Modal
      v-if="dialogIsOpen"
      id="cookie-customise"
      ref="modal"
      width="md"
      :title="$t('privacy-and-terms.cookie_customise.heading')"
      @close="onClose"
      @confirm="onConfirm"
   >
      <div class="text-lighter leading-snug">
         <p>{{ $t("privacy-and-terms.cookie_customise.description") }}</p>

         <!-- Cookie categories -->
         <section
            v-for="cookieCategory in cookieCategories"
            :key="cookieCategory.category"
            class="my-10 last-of-type:mb-0"
         >
            <h2 class="text-regular flex text-lg">
               <span class="grow">{{ cookieCategory.category }} </span>
               <span v-if="cookieCategory.isNecessary" class="mr-2 text-xs text-bolder text-green-60">
                  {{ $t("privacy-and-terms.cookie_customise.always_on") }}
               </span>
            </h2>
            <div class="mb-3 mt-2 h-px w-full bg-grey-20"></div>
            <p class="my-1">{{ cookieCategory.description }}</p>

            <div v-for="cookieItem in cookieCategory.cookies" :key="cookieItem.id" class="mb-2 flex last-of-type:mb-0">
               <!-- cookie selection -->
               <Accordion
                  class="w-full"
                  name="cookie-auth"
                  smallerIcon
                  iconPositionLeft
                  buttonClosedClasses="text-left py-2 text-brand-dark pr-20"
                  buttonOpenClasses="text-left py-2 text-brand-dark pr-20"
               >
                  <template #header>{{ cookieItem.name }}</template>
                  <template #content>
                     <div class="pt-1 text-grey-80">
                        {{ cookieItem.description }}
                     </div>
                  </template>
               </Accordion>

               <div v-if="!cookieCategory.isNecessary" class="flex shrink-0 pt-2">
                  <OnOffToggle
                     :id="cookieItem.id"
                     :data-cy="`cookie-toggle-${cookieItem.id}`"
                     :aria-label="'Allow ' + cookieItem.name + ' cookie'"
                     :value="(cookieItem as ServiceCookie).isActivated"
                     @input="(newValue) => toggleCookieItem(cookieItem.id, newValue)"
                  />
               </div>
            </div>
         </section>
      </div>
   </Modal>
</template>

<script setup lang="ts">
import {
   useCookieSettingsStore,
   type CookieId,
   type CookieCategory,
   type ServiceCookie,
} from "@/stores/cookieSettingsStore";

const cookieSettingsStore = useCookieSettingsStore();
const modal = ref();
const $emit = defineEmits(["close"]);

const cookieCategories = ref<CookieCategory[]>([]);

const props = defineProps<{
   dialogIsOpen: boolean;
}>();

// lifecycle
// -----------------------------------------
// when the dialog is re-opened, reset the state to the current cookie settings
// The user needs to confirm the changes before they are saved
watch(
   () => props.dialogIsOpen,
   (newValue) => {
      if (newValue) {
         setModalCookieCategories();
      }
   }
);
onMounted(() => {
   // Initialise the state with a cloned version of the cookie settings
   // We do this so we can track the changes until the user confirms
   setModalCookieCategories();
});

// methods
// -----------------------------------------
function onClose() {
   $emit("close");
}
// Set the state with a cloned version of the cookie settings from the store
function setModalCookieCategories() {
   cookieCategories.value = JSON.parse(JSON.stringify(cookieSettingsStore.cookieCategories));
}

// Cookie toggle event handler > store the new choices in state until the user confirms
function toggleCookieItem(cookieId: CookieId, newValue: boolean) {
   cookieCategories.value.forEach((category) => {
      if (category.isNecessary) return;

      (category.cookies as ServiceCookie[]).forEach((cookie) => {
         if (cookie.id === cookieId) {
            cookie.isActivated = newValue;
         }
      });
   });
}

// Reduce the cookieCategories to an array of cookie Ids and their new values and send them to the store.
// The store will update the user preferences, set a cookie consented on and reload the page
function onConfirm() {
   const cookieChoices = cookieCategories.value.reduce(
      (acc, category) => {
         if (category.isNecessary) return acc;

         (category.cookies as ServiceCookie[]).forEach((cookie) => {
            acc.push({ id: cookie.id, isActivated: cookie.isActivated });
         });
         return acc;
      },
      [] as { id: CookieId; isActivated: boolean }[]
   );

   cookieSettingsStore.toggleCookieSettings(cookieChoices);
}
</script>
